import React, { useState, useEffect } from "react";
import { Layout } from "@/components/layout";
import { useCurrentBusiness } from "@/hooks/use-current-business";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue,
  SelectGroup,
  SelectLabel
} from "@/components/ui/select";
import { Check, ChevronsUpDown, CalendarIcon, Loader2, PlusCircle } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useLocation, Link } from "wouter";
import { useChartOfAccounts } from "@/hooks/use-chart-of-accounts";
import { useVendors, Vendor } from "@/hooks/use-vendors";

// Transaction category interface
interface TransactionCategory {
  id: number;
  name: string;
  description: string;
  color: string;
  businessId: number | null;
  createdAt: string;
  updatedAt: string;
}

const expenseSchema = z.object({
  date: z.date({
    required_error: "Date is required",
  }),
  description: z.string().min(3, "Description must be at least 3 characters"),
  amount: z.string().min(1, "Amount is required"),
  category: z.string().optional(),
  paymentMethod: z.string().optional(),
  vendor: z.string().optional(),
  notes: z.string().optional(),
});

type ExpenseFormValues = z.infer<typeof expenseSchema>;

export default function CreateExpensePage() {
  const { currentBusiness } = useCurrentBusiness();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const [openVendor, setOpenVendor] = useState(false);
  
  // Get chart of accounts data for the current business
  const { 
    chartOfAccounts, 
    accounts, 
    accountsByType,
    isLoading: isLoadingAccounts 
  } = useChartOfAccounts();
  
  // Fetch vendors for the dropdown
  const {
    data: vendors = [],
    isLoading: isLoadingVendors
  } = useVendors(currentBusiness?.id);
  
  // Manually set expense categories for now, since the API is having issues
  const {
    data: transactionCategories = [],
    isLoading: isLoadingCategories
  } = useQuery<TransactionCategory[]>({
    queryKey: ["/api/transactions/categories"],
    queryFn: async () => {
      try {
        const response = await apiRequest("GET", `/api/transactions/categories`);
        if (!response.ok) {
          console.error("Failed to fetch transaction categories");
          return getDefaultCategories();
        }
        const data = await response.json();
        return Array.isArray(data) ? data : getDefaultCategories();
      } catch (error) {
        console.error("Error fetching categories:", error);
        return getDefaultCategories();
      }
    },
    enabled: true,
  });
  
  // Helper function to get default expense categories
  function getDefaultCategories(): TransactionCategory[] {
    return [
      { id: 10, name: "Salary & Wages", description: "Employee compensation including salaries and wages", color: "#F44336", businessId: null, createdAt: "", updatedAt: "" },
      { id: 11, name: "Rent", description: "Office, retail or other space rental expenses", color: "#E91E63", businessId: null, createdAt: "", updatedAt: "" },
      { id: 12, name: "Utilities", description: "Electricity, water, gas, internet, phone bills", color: "#9C27B0", businessId: null, createdAt: "", updatedAt: "" },
      { id: 13, name: "Office Supplies", description: "Paper, pens, stationery and other office supplies", color: "#673AB7", businessId: null, createdAt: "", updatedAt: "" },
      { id: 14, name: "Software & Technology", description: "Software subscriptions, IT services and hardware", color: "#3F51B5", businessId: null, createdAt: "", updatedAt: "" },
      { id: 15, name: "Marketing & Advertising", description: "Promotional activities, ads, sponsorships", color: "#2196F3", businessId: null, createdAt: "", updatedAt: "" },
      { id: 16, name: "Travel", description: "Airfare, hotels, transportation for business trips", color: "#03A9F4", businessId: null, createdAt: "", updatedAt: "" },
      { id: 17, name: "Meals & Entertainment", description: "Business meals, client entertainment", color: "#00BCD4", businessId: null, createdAt: "", updatedAt: "" },
      { id: 18, name: "Insurance", description: "Business insurance premiums", color: "#009688", businessId: null, createdAt: "", updatedAt: "" },
      { id: 19, name: "Professional Services", description: "Legal, accounting, consulting fees", color: "#4CAF50", businessId: null, createdAt: "", updatedAt: "" },
      { id: 30, name: "Miscellaneous", description: "Expenses that do not fit in other categories", color: "#9E9E9E", businessId: null, createdAt: "", updatedAt: "" }
    ];
  }
  
  // Filter expense categories for the dropdown - removing income related ones
  const expenseCategories = transactionCategories.filter(
    category => !category.name.toLowerCase().includes('income') && 
                !category.name.toLowerCase().includes('revenue') &&
                !category.name.toLowerCase().includes('payment')
  ) || getDefaultCategories();
  
  // Extract expense accounts for the dropdown (from chart of accounts)
  const expenseAccounts = accountsByType?.expense?.filter(account => 
    // Filter out header accounts and inactive accounts
    account.subtype !== 'header' && account.is_active
  ) || [];
  
  console.log("Expense accounts:", expenseAccounts);
  console.log("Expense categories:", expenseCategories);
  console.log("Vendors:", vendors);

  const form = useForm<ExpenseFormValues>({
    resolver: zodResolver(expenseSchema),
    defaultValues: {
      date: new Date(),
      description: "",
      amount: "",
      category: "",
      paymentMethod: "",
      vendor: "",
      notes: "",
    },
  });

  const createExpenseMutation = useMutation({
    mutationFn: async (data: ExpenseFormValues) => {
      if (!currentBusiness) throw new Error("No business selected");
      
      // Validate that the selected category exists in our available categories
      const categoryId = data.category ? parseInt(data.category, 10) : undefined;
      
      if (categoryId) {
        const categoryExists = expenseCategories.some(cat => cat.id === categoryId);
        if (!categoryExists) {
          throw new Error("Invalid expense category selected. Please select a valid category.");
        }
      }
      
      const transactionData = {
        date: data.date,
        description: data.description,
        type: "expense",
        amount: data.amount,
        status: "completed",
        metadata: {
          paymentMethod: data.paymentMethod,
          vendor: data.vendor,
          notes: data.notes,
        },
        categoryId: categoryId,
      };
      
      const response = await apiRequest(
        "POST", 
        `/api/businesses/${currentBusiness.id}/transactions`, 
        transactionData
      );
      
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Expense created successfully",
      });
      queryClient.invalidateQueries({ queryKey: [`/api/businesses/${currentBusiness?.id}/transactions`] });
      // Use setLocation for programmatic navigation
      setLocation("/expenses/expenses");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: ExpenseFormValues) => {
    createExpenseMutation.mutate(data);
  };

  if (!currentBusiness) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[50vh]">
          <h2 className="text-2xl font-semibold mb-2">No Business Selected</h2>
          <p className="text-muted-foreground">Please select a business to create expenses.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto py-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Create Expense</h1>
            <p className="text-muted-foreground mt-1">
              Record a new expense for {currentBusiness.name}
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link href="/expenses/expenses">
              Cancel
            </Link>
          </Button>
        </div>

        <Card className="max-w-3xl mx-auto">
          <CardHeader>
            <CardTitle>Expense Details</CardTitle>
            <CardDescription>
              Enter the details of your expense transaction.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="date"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>Date</FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant={"outline"}
                                className={cn(
                                  "pl-3 text-left font-normal",
                                  !field.value && "text-muted-foreground"
                                )}
                              >
                                {field.value ? (
                                  format(field.value, "PPP")
                                ) : (
                                  <span>Pick a date</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={field.onChange}
                              disabled={(date) =>
                                date > new Date() || date < new Date("1900-01-01")
                              }
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="amount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Amount</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2">$</span>
                            <Input
                              placeholder="0.00"
                              className="pl-8"
                              {...field}
                              type="number"
                              step="0.01"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Brief description of the expense" 
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Category</FormLabel>
                        <Select 
                          onValueChange={field.onChange} 
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a category" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {isLoadingCategories ? (
                              <SelectItem value="loading" disabled>Loading categories...</SelectItem>
                            ) : expenseCategories.length > 0 ? (
                              <>
                                <SelectGroup>
                                  <SelectLabel>Expense Categories</SelectLabel>
                                  {expenseCategories.map(category => (
                                    <SelectItem key={category.id} value={category.id.toString()}>
                                      {category.name}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              </>
                            ) : (
                              <>
                                {/* If no transaction categories are available, fallback to chart of accounts */}
                                {expenseAccounts.length > 0 ? (
                                  <SelectGroup>
                                    <SelectLabel>Chart of Accounts - Expense Categories</SelectLabel>
                                    {expenseAccounts.map(account => (
                                      <SelectItem key={account.id} value={account.id.toString()}>
                                        {account.code} - {account.name}
                                      </SelectItem>
                                    ))}
                                  </SelectGroup>
                                ) : (
                                  <SelectItem value="0" disabled>No expense categories available</SelectItem>
                                )}
                              </>
                            )}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="paymentMethod"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Payment Method</FormLabel>
                        <Select 
                          onValueChange={field.onChange} 
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select payment method" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="credit_card">Credit Card</SelectItem>
                            <SelectItem value="debit_card">Debit Card</SelectItem>
                            <SelectItem value="cash">Cash</SelectItem>
                            <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                            <SelectItem value="check">Check</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="vendor"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Vendor/Payee</FormLabel>
                      <Popover open={openVendor} onOpenChange={setOpenVendor}>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={openVendor}
                              className={cn(
                                "w-full justify-between",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              {field.value
                                ? vendors.find((vendor) => vendor.name === field.value)?.name || field.value
                                : "Select or type vendor name"}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-[400px] p-0">
                          <Command>
                            <CommandInput placeholder="Search vendors..." />
                            <CommandEmpty>
                              {isLoadingVendors ? (
                                <div className="flex items-center justify-center p-4">
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Loading vendors...
                                </div>
                              ) : (
                                <div className="p-4 text-center">
                                  <p className="py-2">No vendors found.</p>
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => setLocation("/expenses/vendors")}
                                    className="mt-2"
                                  >
                                    <PlusCircle className="mr-2 h-4 w-4" />
                                    Create Vendor
                                  </Button>
                                </div>
                              )}
                            </CommandEmpty>
                            <CommandGroup>
                              {vendors.map((vendor) => (
                                <CommandItem
                                  key={vendor.id}
                                  value={vendor.name}
                                  onSelect={() => {
                                    field.onChange(vendor.name);
                                    setOpenVendor(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      vendor.name === field.value
                                        ? "opacity-100"
                                        : "opacity-0"
                                    )}
                                  />
                                  {vendor.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Notes</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Additional details about this expense" 
                          className="min-h-[100px]" 
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-end space-x-4 pt-4">
                  <Button 
                    type="button" 
                    variant="outline"
                    asChild
                  >
                    <Link href="/expenses/expenses">
                      Cancel
                    </Link>
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={createExpenseMutation.isPending}
                  >
                    {createExpenseMutation.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Save Expense
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
